import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { RuianCadastralTerritory } from "@/requests/ruian";
import { getRuianCadastralTerritory } from "@/requests/ruian";
import { ReactNode, useMemo, useState } from "react";
import Autocomplete from "@/components/Autocomplete";
import { InputTooltip } from "@/components/InputTooltip";

interface CadastralTerritoryProps {
  cadastralTerritory: string;
  label: string;
  onSubmit: (selectedTerritory: RuianCadastralTerritory | null) => void;
  helperText: ReactNode;
  cadastralTerritoryInputProps: {
    onChange: () => void;
    value?: string;
    checked?: boolean;
    error?: string;
    onFocus?: () => void;
    onBlur?: () => void;
  };
}

export const CadastralTerritory = ({
  cadastralTerritory,
  label,
  onSubmit,
  helperText,
  cadastralTerritoryInputProps,
}: CadastralTerritoryProps) => {
  const [shownValue, setShownValue] = useState(cadastralTerritory);

  const [debounced] = useDebouncedValue(shownValue, 200);

  const { data, isLoading } = useQuery({
    queryKey: ["cadastral_territories/search", debounced],
    queryFn: () => getRuianCadastralTerritory({ query: debounced }),
    refetchOnWindowFocus: false,
    enabled: debounced !== "",
  });

  const formattedCadastralTerritories = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.map(territory => {
      return {
        value: territory.name,
        label: territory.name,
      };
    });
  }, [data]);

  const isValueFromDropdown = (label: string) => {
    return formattedCadastralTerritories.some(territory => territory.label === label);
  };

  const getSelectedTerritory = (value: string) => {
    if (!data) {
      return undefined;
    }
    return data.find(territory => territory.name === value);
  };

  return (
    <InputTooltip
      tooltipProps={{
        label: helperText,
      }}>
      <Autocomplete
        label={label}
        data={formattedCadastralTerritories}
        {...cadastralTerritoryInputProps}
        value={shownValue}
        onChange={value => {
          setShownValue(value);
          if (!isValueFromDropdown(value)) {
            onSubmit(null);
          }
        }}
        selectFirstOptionOnChange={true}
        onOptionSubmit={value => {
          const selectedTerritory = getSelectedTerritory(value);
          if (!selectedTerritory) {
            return;
          }
          onSubmit(selectedTerritory);
        }}
        isLoading={isLoading}
      />
    </InputTooltip>
  );
};
